import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Modal,
  Label,
  Text,
  Input,
  FormGroup,
  Badge,
  FormMessage
} from '@adminjs/design-system';

type ComplaintStatus = {
  id: number;
  title: string;
  groupName: string;
  status: 'pending' | 'assigned' | 'resolved';
  assignedAt?: string;
  resolvedAt?: string;
};

type StatusFormFields = {
  complaintId: string;
};

const ComplaintStatusModal: React.FC<{ setShowStatusModal: (show: boolean) => void }> = ({ setShowStatusModal }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<ComplaintStatus | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<StatusFormFields>({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const onSubmit = async (data: StatusFormFields) => {
    setError(null);
    setStatus(null);
    setLoading(true);

    const complaintId = data.complaintId;

    try {
      const res = await fetch(`/api/complaints/${complaintId}`);
      if (!res.ok) {
        if (res.status === 404) {
          setError('Complaint not found. Please check the ID and try again.');
        } else {
          setError('Failed to fetch complaint status. Please try again later.');
        }
        setLoading(false);
        return;
      }
      const result = await res.json();
      setStatus(result);
    } catch (err: any) {
      setError(err.message || 'Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      variant='default'
      icon='Search'
      title='Track Complaint Status'
      buttons={[
        {
          label: 'Close',
          onClick: () => setShowStatusModal(false),
          variant: status ? 'primary' : 'default'
        }
      ]}
      onClose={() => setShowStatusModal(false)}
      onOverlayClick={() => setShowStatusModal(false)}
    >
      <Box>
        <Box
          mb="lg"
          p="default"
          style={{
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
            borderRadius: '8px',
            borderLeft: '4px solid #1e40af'
          }}
        >
          <Text style={{ color: '#1f2937', fontSize: '14px' }}>
            Enter your complaint ID to check its current status and track progress.
          </Text>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup error={!!errors.complaintId}>
            <Label required style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>
              Complaint ID
            </Label>
            <Input
              variant="lg"
              type="number"
              {...register('complaintId', {
                required: 'Complaint ID is required',
                min: { value: 1, message: 'Complaint ID must be a positive number' },
              })}
              placeholder="e.g., 12345"
              disabled={loading}
              aria-invalid={!!errors.complaintId}
              aria-describedby={errors.complaintId ? 'error-complaintId' : undefined}
              style={{
                fontSize: '15px',
                padding: '10px 14px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb'
              }}
            />
            {errors.complaintId && <FormMessage>{errors.complaintId.message}</FormMessage>}
          </FormGroup>

          <Box mt="lg">
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
              size="lg"
              style={{
                width: '100%',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: 'bold',
                borderRadius: '8px'
              }}
            >
              {loading ? 'Checking...' : 'Check Status'}
            </Button>
          </Box>

          {error && (
            <Box
              mt="lg"
              p="lg"
              style={{
                background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                borderRadius: '10px',
                border: '2px solid #fca5a5',
                boxShadow: '0 2px 8px rgba(220, 38, 38, 0.15)'
              }}
            >
              <Box display="flex" alignItems="center" style={{ gap: '12px' }}>
                <Box
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                >
                  ⚠
                </Box>
                <Text style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '16px' }}>{error}</Text>
              </Box>
            </Box>
          )}

          {status && (
            <Box
              mt="lg"
              p="xl"
              style={{
                background: 'white',
                borderRadius: '12px',
                border: '2px solid #e5e7eb',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb="lg"
                pb="lg"
                style={{ borderBottom: '2px solid #e5e7eb' }}
              >
                <Text fontSize="h3" fontWeight="bold" style={{ color: '#1f2937' }}>
                  Complaint #{status.id}
                </Text>
                {status.status === 'resolved' ? (
                  <Badge
                    variant="success"
                    size="lg"
                    style={{
                      padding: '8px 20px',
                      fontSize: '15px',
                      fontWeight: 'bold',
                      borderRadius: '20px',
                      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                      border: 'none',
                      boxShadow: '0 2px 8px rgba(5, 150, 105, 0.3)'
                    }}
                  >
                    ✓ Resolved
                  </Badge>
                ) : status.status === 'assigned' ? (
                  <Badge
                    variant="primary"
                    size="lg"
                    style={{
                      padding: '8px 20px',
                      fontSize: '15px',
                      fontWeight: 'bold',
                      borderRadius: '20px',
                      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
                      border: 'none',
                      boxShadow: '0 2px 8px rgba(30, 64, 175, 0.3)'
                    }}
                  >
                    ⏱ Assigned
                  </Badge>
                ) : (
                  <Badge
                    variant="default"
                    size="lg"
                    style={{
                      padding: '8px 20px',
                      fontSize: '15px',
                      fontWeight: 'bold',
                      borderRadius: '20px',
                      background: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
                      border: 'none',
                      boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)'
                    }}
                  >
                    ⏳ Pending
                  </Badge>
                )}
              </Box>

              <Box mb="lg">
                <Text fontSize="xs" style={{ color: '#6b7280', fontWeight: 'bold', letterSpacing: '0.05em', textTransform: 'uppercase' }} mb="xs">
                  Title
                </Text>
                <Text fontSize="default" style={{ color: '#1f2937', lineHeight: '1.6' }}>
                  {status.title}
                </Text>
              </Box>

              <Box mb="lg">
                <Text fontSize="xs" style={{ color: '#6b7280', fontWeight: 'bold', letterSpacing: '0.05em', textTransform: 'uppercase' }} mb="xs">
                  Category
                </Text>
                <Box
                  display="inline-block"
                  px="default"
                  py="xs"
                  style={{
                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                    borderRadius: '8px',
                    color: '#1e40af',
                    fontWeight: '600',
                    fontSize: '14px',
                    padding: '6px 16px',
                    border: '1px solid #bfdbfe'
                  }}
                >
                  {status.groupName}
                </Box>
              </Box>

              {status.status === 'assigned' && status.assignedAt && (
                <Box
                  mt="lg"
                  pt="lg"
                  style={{ borderTop: '2px solid #e5e7eb' }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: '8px' }}
                  >
                    <Box
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: '#1e40af',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        color: 'white'
                      }}
                    >
                      ⏱
                    </Box>
                    <Text fontSize="default" style={{ color: '#6b7280' }}>
                      Assigned on{' '}
                      <strong style={{ color: '#1f2937' }}>
                        {new Date(status.assignedAt).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </strong>
                    </Text>
                  </Box>
                </Box>
              )}
              {status.status === 'resolved' && status.resolvedAt && (
                <Box
                  mt="lg"
                  pt="lg"
                  style={{ borderTop: '2px solid #e5e7eb' }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: '8px' }}
                  >
                    <Box
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: '#10b981',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        color: 'white'
                      }}
                    >
                      ✓
                    </Box>
                    <Text fontSize="default" style={{ color: '#6b7280' }}>
                      Resolved on{' '}
                      <strong style={{ color: '#1f2937' }}>
                        {new Date(status.resolvedAt).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </strong>
                    </Text>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </form>
      </Box>
    </Modal>
  );
};

export default ComplaintStatusModal;
